// ---------------------------------------------------------------------------
// Quoted-history trimming — the "…" behaviour every mail client has. A reply
// carries the whole conversation below it, so a thread view that renders every
// body in full shows the same text N times and buries what is actually new.
//
// Detection is heuristic (there is no standard marker) and deliberately
// conservative: we cut at the FIRST recognised quote marker and only when what
// remains above it still has visible content, so a message whose entire body
// looks like a quote is left alone rather than rendered empty.
// ---------------------------------------------------------------------------

/**
 * Container elements the major clients emit around the quoted chain. Matching
 * one of these is the strongest signal available — stronger than any text
 * pattern, which is why they are tried first.
 */
const QUOTE_CONTAINER_SELECTOR = [
  ".gmail_quote", // Gmail (the wrapper div; `.gmail_attr` attribution is inside it)
  ".gmail_quote_container",
  ".moz-cite-prefix", // Thunderbird attribution line, sibling of its blockquote
  "blockquote[type='cite']", // Apple Mail, Thunderbird
  "#divRplyFwdMsg", // Outlook desktop reply/forward separator
  "#appendonsend", // Outlook web
  "#mail-editor-reference-message-container", // Yahoo
  ".OutlookMessageHeader",
  "hr#stopSpelling", // Outlook web divider above the quoted chain
].join(",");

/** "On Tue, 2 Sep 2025 at 10:04, Ada <ada@x> wrote:" and its localised cousins. */
const ATTRIBUTION_LINE = /^\s*on\b[\s\S]{0,400}\bwrote:\s*$/i;
/** "-----Original Message-----", "---------- Forwarded message ----------". */
const SEPARATOR_LINE = /^\s*-{2,}\s*(original message|forwarded message)\s*-{2,}\s*$/i;
/** Outlook's horizontal rule rendered as underscores. */
const UNDERSCORE_RULE = /^\s*_{5,}\s*$/;
/**
 * Outlook's quoted header block — "From: … Sent: … Subject: …" — anchored at
 * the START of the element's text. The anchor is what makes this safe to run
 * against wrapper elements: a `<div>` holding the reply *and* the quote starts
 * with the reply, so only the quote's own container can match.
 */
const QUOTED_HEADER_BLOCK = /^from:\s[\s\S]{0,400}?\b(sent|date):\s/i;
/** Plain-text form of the same block, matched a line at a time. */
const HEADER_FROM = /^\s*from:\s*\S/i;
const HEADER_FOLLOW = /^\s*(sent|date|to|subject):/i;

/** True when an element's text is where the quoted chain begins. */
function isQuoteStartText(text: string): boolean {
  const collapsed = text.replace(/\s+/g, " ").trim();
  if (!collapsed) return false;
  return (
    ATTRIBUTION_LINE.test(collapsed) ||
    SEPARATOR_LINE.test(collapsed) ||
    QUOTED_HEADER_BLOCK.test(collapsed)
  );
}

/** Text of everything that precedes `node` inside `parent`. */
function textBefore(parent: Element, node: Node): string {
  let out = "";
  for (const child of Array.from(parent.childNodes)) {
    if (child === node || child.contains(node)) break;
    out += child.textContent ?? "";
  }
  return out;
}

/**
 * The topmost ancestor that can be cut without losing content: lift while the
 * parent holds nothing but the quote above it. Emails routinely wrap the whole
 * body in one `<div>`, so lifting unconditionally would delete the reply too.
 */
function liftCutPoint(body: HTMLElement, node: Element): Element {
  let current = node;
  while (current.parentElement && current.parentElement !== body) {
    if (textBefore(current.parentElement, current).trim() !== "") break;
    current = current.parentElement;
  }
  return current;
}

/** First element in document order that marks the start of the quoted chain. */
function findQuoteMarker(body: HTMLElement): Element | null {
  const container = body.querySelector(QUOTE_CONTAINER_SELECTOR);
  if (container) return container;

  // No client marker: fall back to what the composer left behind — an
  // attribution line, a separator rule, or Outlook's quoted header block.
  const walker = body.ownerDocument.createTreeWalker(body, NodeFilter.SHOW_ELEMENT);
  let node = walker.nextNode() as Element | null;
  while (node) {
    if (isQuoteStartText(node.textContent ?? "")) return node;
    node = walker.nextNode() as Element | null;
  }
  return null;
}

/**
 * Everything that would still be visible after cutting at `cut`: the nodes that
 * precede it at every level between it and the body. Checked BEFORE removing
 * anything, so a message that reads entirely as a quote is left untouched
 * rather than partially dismantled and put back.
 */
function survivesTrim(body: HTMLElement, cut: Element): boolean {
  let text = "";
  for (let node: Element | null = cut; node && node !== body; node = node.parentElement) {
    const parent: Element | null = node.parentElement;
    if (!parent) break;
    for (const child of Array.from(parent.childNodes) as ChildNode[]) {
      if (child === node) break;
      text += child.textContent ?? "";
      const element = child.nodeType === 1 ? (child as Element) : null;
      if (element && (element.tagName === "IMG" || element.querySelector("img"))) return true;
    }
  }
  return text.trim() !== "";
}

/**
 * Split an email body document into the reply and the history below it.
 *
 * `doc` is trimmed IN PLACE to the reply; the returned document is a clone
 * holding only the quoted chain (same `<head>`, so the sender's styles still
 * apply). Returning the two halves — rather than the reply and the whole
 * original — is what lets the caller keep its reveal control between them, the
 * way a mail client does, instead of stranding it below the history.
 *
 * @returns the quoted-only document, or null when there is no quoted history
 *   or when trimming would have emptied the message.
 */
export function splitQuotedHistory(doc: Document): Document | null {
  const body = doc.body;
  if (!body) return null;
  const marker = findQuoteMarker(body);
  if (!marker) return null;

  const cut = liftCutPoint(body, marker);
  if (!survivesTrim(body, cut)) return null;

  // The clone is split by the SAME rules on identical input, so its cut point
  // is the counterpart of this one — no index bookkeeping to keep in step.
  const quotedDoc = doc.cloneNode(true) as Document;
  const quotedMarker = quotedDoc.body ? findQuoteMarker(quotedDoc.body) : null;
  if (quotedDoc.body && quotedMarker) {
    const quotedCut = liftCutPoint(quotedDoc.body, quotedMarker);
    // Mirror image of the trim below: drop what PRECEDES the cut at every level.
    for (
      let node: Element | null = quotedCut;
      node && node !== quotedDoc.body;
      node = node.parentElement
    ) {
      let sibling: ChildNode | null = node.previousSibling;
      while (sibling) {
        const previous: ChildNode | null = sibling.previousSibling;
        sibling.remove();
        sibling = previous;
      }
    }
  }

  // Everything from the cut point down the document is history — including the
  // trailing siblings of each ancestor, which is where a wrapped quote's
  // remainder lives.
  for (let node: Element | null = cut; node && node !== body; node = node.parentElement) {
    let sibling: ChildNode | null = node.nextSibling;
    while (sibling) {
      const next: ChildNode | null = sibling.nextSibling;
      sibling.remove();
      sibling = next;
    }
  }
  cut.remove();
  return quotedDoc;
}

/**
 * Plain-text counterpart: the index of the first line that starts the quoted
 * chain, or -1. Handles `>` quoting, attribution lines (which wrap across
 * lines), Outlook's underscore rule and its `From:`/`Sent:` header block.
 */
function quotedLineIndex(lines: string[]): number {
  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i] ?? "";
    if (/^\s*>/.test(line)) return i;
    if (SEPARATOR_LINE.test(line)) return i;
    if (UNDERSCORE_RULE.test(line)) return i;
    if (HEADER_FROM.test(line) && HEADER_FOLLOW.test(lines[i + 1] ?? "")) return i;
    if (/^\s*on\b/i.test(line)) {
      // The attribution often wraps; join up to three lines before matching.
      const joined = lines
        .slice(i, i + 3)
        .join(" ")
        .replace(/\s+/g, " ");
      if (/^\s*on\b[\s\S]{0,400}\bwrote:/i.test(joined)) return i;
    }
  }
  return -1;
}

/**
 * Split a plain-text body into what the sender wrote and the history below it.
 * `quoted` is null when there is no quoted chain, or when trimming would leave
 * nothing to read.
 */
export function splitQuotedText(text: string): { visible: string; quoted: string | null } {
  const lines = text.split("\n");
  const index = quotedLineIndex(lines);
  if (index <= 0) return { visible: text, quoted: null };
  const visible = lines.slice(0, index).join("\n").replace(/\s+$/, "");
  if (visible.trim() === "") return { visible: text, quoted: null };
  return { visible, quoted: lines.slice(index).join("\n") };
}
